// MACHINERY V2 (Stroje) — catalog model detail page (M2.8).
//
// Spec: data package §11 (detail route + states). Reference impl:
// components/CatalogModelDetailPage.tsx; artboard 0902 — 380px identity column
// on the left, the spec sheet on the right.
//
// >>> §2.3 DEVIATION, STATED HERE TOO: THE SPEC SHEET IS NOT `MachineSpecs`. <<<
// The M2.8 ticket row says "reuse `MachineSpecs`". That component imports MUI
// and `dt` (MachineSpecs.tsx:2-16 and :20) and cannot enter the v2 import graph
// under §3. The MUI-free v2 spec stack shipped at M2.5 does the same job —
// `resolveSpecSheet` for the classification/description rules, `SpecSheetView`
// for the toolbar + sections, `convertSpec` inside `SpecRow` for the
// metric/imperial toggle — so the page reuses THAT. Full reasoning in
// profile/tabs/SpecSheetView.tsx's header.
//
// >>> `hasFetchedRef` AND `clearSelectedModel` ARE BOTH PRESERVED. <<<
// `selectedModel` is a SHARED slice slot: the profile's Špecifikácie tab reads
// it too. v1 guards the "Model not found" flash with `hasFetchedRef` (the slot
// is empty for one render before the first fetch resolves) and clears the slot
// on unmount so a later visit cannot render a stale model. BOTH are kept — and
// the id guard the v2 tab added is applied here as well, so an arriving
// response for a PREVIOUS model never paints under this one. (The tab
// deliberately does NOT clear on unmount; this page owns that lifecycle. See
// SpecificationsTabV2.tsx:16-26.)
//
// §11 "Error: thunks rejected only set loading=false — NO error UI, renders as
// empty / 'Model not found'." Fixed here the same way the browse page fixes it:
// the failure is tracked off the dispatch promise and gets a real RetryNotice.
// "Model not found" was also a HARDCODED English string (§11 States) and is now
// a key in both locales.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import usePermission from "../../../../shared/hooks/usePermission";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Modules } from "../../../../core/models/PermissionModels";
import { CatalogModelListItem } from "../../models/models";
import { clearSelectedModel, getCatalogModelDetail } from "../../machineCatalogSlice";
import MachineryToastHost from "../feedback/MachineryToastHost";
import RetryNotice from "../feedback/RetryNotice";
import ReadinessPopupV2 from "../readiness/ReadinessPopupV2";
import {
  deriveReadinessPresentation,
  readinessSummaryStrings,
} from "../readiness/readinessPresentation";
import { resolveSpecSheet } from "../profile/tabs/specResolve";
import SpecSheetView from "../profile/tabs/SpecSheetView";
import { SkeletonList, TabCard, TabEmpty } from "../profile/tabs/tabAtoms";
import CatalogAddDialogV2 from "./CatalogAddDialogV2";
import CatalogDetailAside from "./CatalogDetailAside";
import { CatalogBreadcrumb, CatalogTitle, catalogAddedPath } from "./catalogChrome";
import { detailAsListItem } from "./catalogDetailAdapter";

const CatalogDetailV2: React.FC = () => {
  const { modelId } = useParams<{ modelId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate, getCurrentLanguage } = useTranslation(["myMachinery", "common"]);
  const { isPhone } = useBreakpoint();
  const { canWrite } = usePermission(Modules.Machinery);

  const selectedModel = useAppSelector((s) => s.machineCatalog.selectedModel);
  const loading = useAppSelector((s) => s.machineCatalog.selectedModelLoading);

  const [failed, setFailed] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const hasFetchedRef = useRef(false);

  const load = useCallback(() => {
    if (!modelId) return;
    setFailed(false);
    hasFetchedRef.current = true;
    dispatch(getCatalogModelDetail(modelId))
      .unwrap()
      .catch(() => setFailed(true));
  }, [dispatch, modelId]);

  useEffect(() => {
    load();
    return () => {
      dispatch(clearSelectedModel());
    };
  }, [load, dispatch]);

  /** The shared-slot id guard — never paint another model's data here. */
  const model = selectedModel?.id === modelId ? selectedModel : undefined;
  const currentLang = getCurrentLanguage();

  const summaryStrings = useMemo(() => readinessSummaryStrings(translate), [translate]);
  const presentation = useMemo(
    () => (model ? deriveReadinessPresentation(model, undefined, summaryStrings) : null),
    [model, summaryStrings]
  );

  const sheet = useMemo(
    () => resolveSpecSheet(model?.specSections, currentLang),
    [model, currentLang]
  );

  /** The add dialog and the readiness popup both speak the LIST-item shape;
   *  the detail VM is a superset minus a few flattened names (§11's closing
   *  paragraph). One adapter, so neither has to know which page opened it. */
  const asListItem: CatalogModelListItem | null = useMemo(
    () => (model ? detailAsListItem(model) : null),
    [model]
  );

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0, flexDirection: "column" }}>
      <CatalogBreadcrumb current={model ? model.modelName : undefined} />
      <CatalogTitle />

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 24px 24px" }}>
        {failed && (
          <div style={{ marginBottom: 12 }}>
            <RetryNotice
              title={translate("myMachinery:v2.catalog.loadFailed")}
              hint={translate("myMachinery:v2.tabs.loadFailedHint")}
              onRetry={load}
              busy={loading}
            />
          </div>
        )}

        {!model && (loading || !hasFetchedRef.current) && !failed && (
          <TabCard>
            <SkeletonList rows={5} height={40} />
          </TabCard>
        )}

        {/* Only ONCE a fetch has actually happened and did not fail — the
            `hasFetchedRef` guard is what stops the flash v1 documented. */}
        {!model && !loading && !failed && hasFetchedRef.current && (
          <TabEmpty title={translate("myMachinery:v2.catalog.modelNotFound")} />
        )}

        {model && presentation && asListItem && (
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "flex-start",
              minWidth: 0,
              // M2.11 owns the tablet/phone variants; wrapping keeps the two
              // columns usable at narrow widths until then.
              flexWrap: "wrap",
            }}
          >
            <CatalogDetailAside
              model={model}
              presentation={presentation}
              canWrite={canWrite}
              onAdd={() => setAddOpen(true)}
              onOpenReadiness={() => setReadinessOpen(true)}
            />

            <div
              style={{
                flex: 1,
                minWidth: 320,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <SpecSheetView
                key={model.id}
                sheet={sheet}
                label={translate("myMachinery:specifications")}
                phone={isPhone}
              />
            </div>
          </div>
        )}
      </div>

      {/* Page-level siblings, outside every clickable ancestor — same rule as
          the browse page and FleetPage.tsx:246-251. */}
      {readinessOpen && model && presentation && (
        <ReadinessPopupV2
          open
          onOpenChange={(o) => {
            if (!o) setReadinessOpen(false);
          }}
          presentation={presentation}
          machineOrModelName={model.modelName}
          oemPartnerName={model.oemPartnerName}
          manufacturerName={model.manufacturer?.name}
        />
      )}

      {/* A successful add LEAVES this page — see `catalogAddedPath` in
          catalogChrome.tsx. Navigating away is right here precisely because the
          user's task on a model detail page has just completed, and it gives
          both catalog entry points ONE predictable exit rather than two
          different silences. */}
      {addOpen && asListItem && (
        <CatalogAddDialogV2
          open
          model={asListItem}
          onClose={() => setAddOpen(false)}
          onCreated={(id) => navigate(catalogAddedPath(id))}
        />
      )}

      <MachineryToastHost />
    </div>
  );
};

export default CatalogDetailV2;
